import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { WahooWorkoutType, WorkoutProfileType } from "@WahooFitness/cloud-client-types";
import {
  useDialogContext,
  useSnackbarContext,
  useNativeMessagingContext,
  useUserContext,
  useGuardedUpdater,
} from "@WahooFitness/wahoo-offline-mfe";
import { t } from "@lingui/macro";
import { DebouncedFunc } from "lodash";
import { WSMWorkoutProfile } from "@WahooFitness/wsm-native/dist/esm/types/workout_profile";
import { KeyedMutator } from "swr";
import { useWorkoutProfilesSource } from "@/hooks/WorkoutProfiles/useWorkoutProfilesSource";
import {
  useWorkoutAlertsSource,
  IWorkoutAlertSource,
} from "@/hooks/WorkoutProfiles/useWorkoutAlertsSource";
import { useDefaultWorkoutTypes } from "@/hooks/WorkoutProfiles/useDefaultWorkoutTypes";

interface IWorkoutProfilesContextProps {
  workoutProfiles: WSMWorkoutProfile[];
  workoutProfilesLoading: boolean;
  workoutProfileNames: string[];
  createProfile: (
    workoutTypeId: number,
    name: string,
    rest?: any
  ) => Promise<WSMWorkoutProfile | undefined>;
  copyProfile: (profileId: string, updatedName?: string) => Promise<WSMWorkoutProfile | undefined>;
  updateProfile: (
    workoutProfileId: string,
    field: keyof WorkoutProfileType,
    value: any
  ) => Promise<void>;
  updateProfileWithObject: (
    workoutProfileId: string,
    update: Partial<WorkoutProfileType>
  ) => Promise<void>;
  workoutProfilesError?: unknown;
  deleteProfile: (profileId: string) => Promise<void>;
  resetProfile: (profileId: string) => Promise<WSMWorkoutProfile | null | undefined>;
  mutateProfileState: (profile: WSMWorkoutProfile) => void;
  selectedAssociationList: string[] | undefined;
  setSelectedAssociationList: (associationList?: string[]) => void;
  updateZoneSetAssociations: (
    profileField: keyof WorkoutProfileType,
    zoneSetId: number,
    primaryZoneSetId: number | undefined,
    callback?: () => void
  ) => Promise<void>;
  getWorkoutProfile: (workoutProfileId: string) => WSMWorkoutProfile | undefined;
  getWorkoutProfilePages: (workoutProfileId: string) => Promise<Uint8Array | undefined>;
  updateWorkoutProfilePagesDebounce: DebouncedFunc<
    (profileId: string, pagesBuffer: Uint8Array) => Promise<void>
  >;
  updateWorkoutProfilePagesImmediately: (
    profileId: string,
    pagesBuffer: Uint8Array
  ) => Promise<void | null>;
  disablePubSubUpdates: () => void;
  enablePubSubUpdates: () => void;
  mutateWorkoutProfiles: KeyedMutator<WSMWorkoutProfile[]>;
  useWorkoutAlerts: (workoutProfileExternalId: string) => IWorkoutAlertSource;
  profileUpdateInProgress: boolean;
}

export const WorkoutProfilesContext = createContext<IWorkoutProfilesContextProps | undefined>(
  undefined
);

export const WorkoutProfilesContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleClose } = useDialogContext();
  const { enqueueSnackbar } = useSnackbarContext();
  const { addRefreshListener, removeRefreshListener } = useNativeMessagingContext();
  const { defaultWorkoutTypesMap } = useDefaultWorkoutTypes();
  const { update, updateInProgress } = useGuardedUpdater();

  // state
  const [selectedAssociationList, setSelectedAssociationList] = useState<string[] | undefined>(
    undefined
  );

  const {
    workoutProfiles: workoutProfilesData,
    workoutProfilesError,
    workoutProfilesAreLoading: workoutProfilesLoading,
    reloadWorkoutProfiles,
    getWorkoutProfile,
    createWorkoutProfile,
    deleteWorkoutProfile,
    resetWorkoutProfile,
    editWorkoutProfile,
    updateWorkoutProfilePagesImmediately,
    updateWorkoutProfilePagesDebounce,
    getWorkoutProfilePages,
    setIgnoreWorkoutProfileUpdates,
  } = useWorkoutProfilesSource();

  useEffect(() => {
    const refreshListener = addRefreshListener(reloadWorkoutProfiles);
    return () => removeRefreshListener(refreshListener);
  }, [addRefreshListener, reloadWorkoutProfiles, removeRefreshListener]);

  const deleteProfile = useCallback(
    async (profileId: string) => {
      await deleteWorkoutProfile(profileId);
      handleClose();
    },
    [deleteWorkoutProfile, handleClose]
  );

  const generateName = useCallback(
    (name: string): string => {
      if (workoutProfilesData?.some((wp) => wp.name === name)) {
        const suffix = name.match(/\s*\((\d+)\)$/);
        const base = suffix ? name.slice(0, -suffix[0].length) : name;
        const count = suffix ? +suffix[1] + 1 : 2;
        const newName = `${base} (${count})`;
        return generateName(newName);
      }
      return name;
    },
    [workoutProfilesData]
  );

  const createProfile = useCallback(
    async (
      workoutTypeId: WahooWorkoutType,
      name: string,
      rest?: any,
      workout_pages?: Uint8Array,
      generateNewName: boolean = true
    ) => {
      const newName = generateNewName ? generateName(name) : name;
      const defaults = defaultWorkoutTypesMap?.[workoutTypeId];
      // cloud will throw an error if a parameter gets sent as null, setting to undefined does not send them.
      if (rest) {
        Object.keys(rest).forEach((key) => (rest[key] = rest[key] ?? undefined));
      }
      const newProfile = await createWorkoutProfile({
        ...defaults,
        ...rest,
        name: newName,
        workout_type_id: workoutTypeId,
      });
      if (!newProfile) {
        return;
      }
      if (workout_pages) {
        await updateWorkoutProfilePagesImmediately(newProfile.external_id, workout_pages);
      }
      return newProfile;
    },
    [
      createWorkoutProfile,
      defaultWorkoutTypesMap,
      generateName,
      updateWorkoutProfilePagesImmediately,
    ]
  );

  const copyProfile = useCallback(
    async (profileId: string, updatedName?: string) => {
      const existingProfile = getWorkoutProfile(profileId);
      if (existingProfile) {
        const {
          workout_type_id,
          name: existingName,
          // remove these fields from the copy
          id: _id,
          workout_pages_file: _workout_pages_file,
          permanent: _permanent,
          external_id: _external_id,
          token: _token,
          workout_alerts: _workout_alerts,
          workout_alert_ids: _workout_alert_ids,
          "#dbId": _dbId,
          "#deleted": _deleted,
          "#display_cfg": _display_cfg,
          ...rest
        } = existingProfile;
        const workoutPages = await getWorkoutProfilePages(profileId);
        const name = updatedName ?? existingName;
        return createProfile(Number(workout_type_id), name, rest, workoutPages);
      }
    },
    [createProfile, getWorkoutProfile, getWorkoutProfilePages]
  );

  const mutateProfileState = useCallback(
    (newProfile: WSMWorkoutProfile) => {
      const newWorkoutProfiles: WSMWorkoutProfile[] = [...(workoutProfilesData || [])];
      const existingProfileIndex =
        workoutProfilesData?.findIndex(
          (profile) => profile.external_id === newProfile.external_id
        ) ?? -1;
      if (existingProfileIndex >= 0) {
        newWorkoutProfiles[existingProfileIndex] = newProfile;
      } else {
        newWorkoutProfiles.push(newProfile);
        enqueueSnackbar({
          message: t`Workout Profile added!`,
          severity: "success",
        });
      }
      reloadWorkoutProfiles(newWorkoutProfiles, { revalidate: false });
    },
    [enqueueSnackbar, reloadWorkoutProfiles, workoutProfilesData]
  );

  const updateProfileWithObject = useCallback(
    async (profileId: string, profileUpdates: Partial<WorkoutProfileType>) => {
      const existingProfile = getWorkoutProfile(profileId);
      if (!existingProfile) {
        return;
      }
      update(
        () => {
          return editWorkoutProfile(profileId, profileUpdates);
        },
        {
          afterUpdate: (updatedProfile) => {
            if (!updatedProfile) {
              return;
            }
            // the endpoint for updating workout profiles doesn't return alerts,
            // but it also can't be used to update alerts, so they should be the same as before the update.
            updatedProfile.workout_alerts = existingProfile.workout_alerts;
            mutateProfileState(updatedProfile);
          },
        }
      );
    },
    [editWorkoutProfile, getWorkoutProfile, mutateProfileState, update]
  );

  const updateProfile = useCallback(
    async (profileId: string, field: keyof WorkoutProfileType, value: any) => {
      return updateProfileWithObject(profileId, { [field]: value });
    },
    [updateProfileWithObject]
  );

  const resetProfile = useCallback(
    async (profileId: string) => {
      const updatedProfile = await resetWorkoutProfile(profileId);
      if (!updatedProfile) {
        return;
      }
      updatedProfile.workout_alerts = [];
      mutateProfileState(updatedProfile);
      return updatedProfile;
    },
    [mutateProfileState, resetWorkoutProfile]
  );

  const updateZoneSetAssociations = useCallback(
    async (
      profileField: keyof WorkoutProfileType,
      zoneSetId: number,
      primaryZoneSetId: number | undefined,
      callback = () => {}
    ) => {
      if (selectedAssociationList) {
        for (const profile of workoutProfilesData || []) {
          // if it's selected and it's already associated, do nothing
          // if it's selected and it's not yet associated, associate it
          if (
            selectedAssociationList.includes(profile.external_id) &&
            profile[profileField] !== zoneSetId
          ) {
            await updateProfile(profile.external_id, profileField, zoneSetId);
          }
          // if it's not selected but is associated with this zone set, change the association to the default
          if (
            !selectedAssociationList.includes(profile.external_id) &&
            profile[profileField] === zoneSetId
          ) {
            await updateProfile(profile.external_id, profileField, primaryZoneSetId);
          }
        }
        reloadWorkoutProfiles();
      }
      callback();
    },
    [reloadWorkoutProfiles, selectedAssociationList, updateProfile, workoutProfilesData]
  );

  const workoutProfileNames = useMemo(
    () => workoutProfilesData?.map((profile) => profile.name) || [],
    [workoutProfilesData]
  );

  const { userIsLoading } = useUserContext();

  const disablePubSubUpdates = useCallback(() => {
    setIgnoreWorkoutProfileUpdates(true);
  }, [setIgnoreWorkoutProfileUpdates]);

  const enablePubSubUpdates = useCallback(() => {
    setIgnoreWorkoutProfileUpdates(false);
  }, [setIgnoreWorkoutProfileUpdates]);

  return (
    <WorkoutProfilesContext.Provider
      value={{
        workoutProfiles: workoutProfilesData || [],
        workoutProfilesLoading: workoutProfilesLoading || userIsLoading,
        workoutProfileNames,
        createProfile,
        copyProfile,
        updateProfile,
        updateProfileWithObject,
        workoutProfilesError,
        deleteProfile,
        mutateProfileState,
        resetProfile,
        selectedAssociationList,
        setSelectedAssociationList,
        updateZoneSetAssociations,
        getWorkoutProfile,
        getWorkoutProfilePages,
        updateWorkoutProfilePagesImmediately,
        updateWorkoutProfilePagesDebounce,
        disablePubSubUpdates,
        enablePubSubUpdates,
        mutateWorkoutProfiles: reloadWorkoutProfiles,
        useWorkoutAlerts: useWorkoutAlertsSource,
        profileUpdateInProgress: updateInProgress,
      }}
    >
      {children}
    </WorkoutProfilesContext.Provider>
  );
};
